import React from "react";
import { ServiceLine } from "../xml-document-parser.service";

interface ServicesTableProps {
	services: ServiceLine[];
	type: 'kostenvoranschlag' | 'lieferschein';
	showVat?: boolean;
}

export const ServicesTable: React.FC<ServicesTableProps> = ({ services, type, showVat = false }) => {
	const formatNumber = (num: number | undefined, decimals: number = 2): string => {
		if (num === undefined || num === null) return '';
		return num.toFixed(decimals).replace('.', '.');
	};

	const formatDate = (date: string | undefined): string => {
		if (!date) return '';
		// Already formatted by parser
		return date;
	};

	if (type === 'kostenvoranschlag') {
		return (
			<div className="overflow-x-auto">
				<table className="w-full text-xs border-collapse">
					<thead>
						<tr className="border-b-2 border-gray-300">
							<th className="text-left p-1">Datum</th>
							<th className="text-left p-1">Tarif</th>
							<th className="text-left p-1">Ziffer</th>
							<th className="text-left p-1">Bezugsziffer</th>
							<th className="text-right p-1">Anzahl</th>
							<th className="text-right p-1">TPW</th>
							<th className="text-right p-1">TP</th>
							<th className="text-right p-1">extFaktor</th>
							<th className="text-right p-1">Betrag</th>
						</tr>
					</thead>
					<tbody>
						{services.map((service, idx) => (
							<React.Fragment key={service.recordId || idx}>
								<tr className="border-b border-gray-200">
									<td className="p-1">{formatDate(service.date)}</td>
									<td className="p-1">{service.tariffType}</td>
									<td className="p-1">{service.code}</td>
									<td className="p-1">{service.referenceNumber || ''}</td>
									<td className="text-right p-1">{formatNumber(service.quantity)}</td>
									<td className="text-right p-1">{formatNumber(service.unitFactor)}</td>
									<td className="text-right p-1">{formatNumber(service.unit)}</td>
									<td className="text-right p-1">{formatNumber(service.externalFactor || 1)}</td>
									<td className="text-right p-1 font-medium">{formatNumber(service.amount)}</td>
								</tr>
								<tr>
									<td colSpan={9} className="px-1 pb-1 text-gray-700">
										{service.name}
										{service.remark && (
											<div className="text-xs text-gray-600 italic mt-1">{service.remark}</div>
										)}
									</td>
								</tr>
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	// Lieferschein format
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-xs border-collapse">
				<thead>
					<tr className="border-b-2 border-gray-300">
						<th className="text-left p-1">Datum</th>
						<th className="text-left p-1">Tarif</th>
						<th className="text-left p-1">Tarifziffer</th>
						<th className="text-left p-1">Bezugsziffer</th>
						<th className="text-center p-1">Si</th>
						<th className="text-center p-1">St</th>
						<th className="text-right p-1">Anzahl</th>
						<th className="text-right p-1">TP AL</th>
						<th className="text-right p-1">f AL</th>
						<th className="text-right p-1">TPW AL</th>
						<th className="text-right p-1">TP TL</th>
						<th className="text-right p-1">f TL</th>
						<th className="text-right p-1">TPW TL</th>
						<th className="text-right p-1">A</th>
						<th className="text-right p-1">V</th>
						<th className="text-right p-1">M</th>
						<th className="text-right p-1">Betrag</th>
					</tr>
				</thead>
				<tbody>
					{services.map((service, idx) => (
						<React.Fragment key={service.recordId || idx}>
							<tr className="border-b border-gray-200">
								<td className="p-1">{formatDate(service.date)}</td>
								<td className="p-1">{service.tariffType}</td>
								<td className="p-1">{service.code}</td>
								<td className="p-1">{service.referenceNumber || ''}</td>
								<td className="text-center p-1">{service.session || ''}</td>
								<td className="text-center p-1">{service.bodyLocation || ''}</td>
								<td className="text-right p-1">{formatNumber(service.quantity)}</td>
								<td className="text-right p-1">{formatNumber(service.unitMt)}</td>
								<td className="text-right p-1">{formatNumber(service.unitFactorMt || 1)}</td>
								<td className="text-right p-1">{formatNumber(service.amountMt)}</td>
								<td className="text-right p-1">{formatNumber(service.unitTt)}</td>
								<td className="text-right p-1">{formatNumber(service.unitFactorTt || 1)}</td>
								<td className="text-right p-1">{formatNumber(service.amountTt)}</td>
								<td className="text-center p-1">-</td>
								<td className="text-center p-1">-</td>
								<td className="text-center p-1">{service.providerId || ''}</td>
								<td className="text-right p-1 font-medium">{formatNumber(service.amount)}</td>
							</tr>
							<tr>
								<td colSpan={17} className="px-1 pb-1 text-gray-700">
									{service.name}
									{service.remark && (
										<div className="text-xs text-gray-600 italic mt-1">{service.remark}</div>
									)}
								</td>
							</tr>
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};

interface ServicesSummaryProps {
	servicesCount: number;
	servicesTotal?: number;
	subtotal: number;
	vatAmount?: number;
	vatRate?: number;
	total: number;
	currency: string;
	amountDue?: number;
}

export const ServicesSummary: React.FC<ServicesSummaryProps> = ({
	servicesCount,
	servicesTotal,
	subtotal,
	vatAmount,
	vatRate,
	total,
	currency,
	amountDue
}) => {
	const formatAmount = (amount: number): string => {
		return `${amount.toFixed(2)} ${currency}`;
	};

	return (
		<div className="mt-4 border-t-2 border-gray-300 pt-2">
			<div className="flex justify-between text-sm">
				<div>
					<div className="text-gray-600">
						Totalbetrag aller {servicesCount} Leistungen:
					</div>
					{vatAmount !== undefined && vatAmount > 0 && (
						<>
							<div className="text-gray-600">MwSt {vatRate?.toFixed(2) || '0.00'}%:</div>
							<div className="font-semibold">Gesamttotal:</div>
						</>
					)}
					{amountDue !== undefined && (
						<div className="font-semibold">Fälliger Betrag:</div>
					)}
				</div>
				<div className="text-right">
					<div className="font-medium">{formatAmount(servicesTotal || subtotal)}</div>
					{vatAmount !== undefined && vatAmount > 0 && (
						<>
							<div>{formatAmount(vatAmount)}</div>
							<div className="font-semibold">{formatAmount(total)}</div>
						</>
					)}
					{amountDue !== undefined && (
						<div className="font-semibold">{formatAmount(amountDue)}</div>
					)}
				</div>
			</div>
			<div className="text-xs text-gray-600 mt-2">
				Die Kosten beinhalten alle Zuschläge, auch die gesetzlichen.
			</div>
		</div>
	);
};